/* eslint-disable react-hooks/refs */
import { useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

const sanitizeValue = (value: string) => {
  return value.replace(/[<>"'&]/g, (match) => {
    const escapeMap: Record<string, string> = {
      '<': '%3C',
      '>': '%3E',
      '"': '%22',
      "'": '%27',
      '&': '%26',
    }
    return escapeMap[match]
  })
}

export interface URLReplaceOption {
  replaceCurrentHistoryEntry?: boolean
}

export interface ReplaceParamsOptions<TKey extends string = string> extends URLReplaceOption {
  remove?: TKey[]
  update?: [TKey, string][]
}

export interface BaseUrlParamsHookResult {
  urlParams: Record<string, string | null>
  updateParam: (param: string, value: string, opts?: URLReplaceOption) => void
  removeParam: (param: string) => void
  replaceParams: (params: ReplaceParamsOptions) => void
}

export const useBaseUrlParams = (shouldCheckUnsavedChanges = false): BaseUrlParamsHookResult => {
  const [searchParams, setSearchParams] = useSearchParams()
  const paramsRef = useRef<Record<string, string | null>>({})
  const pendingSearchRef = useRef<string | null>(null)

  /**
   * Dispatches `checkUnsavedChanges` so guard hooks can intercept the navigation.
   * Returns true if no listener prevented it (caller should run `replay` immediately);
   * returns false when a guard claimed the event and will invoke `replay` itself
   * after the user confirms.
   */
  const checkUnsavedChanges = useCallback(
    (replay: () => void, newSection?: string, paramName?: string) => {
      if (!shouldCheckUnsavedChanges) return true
      const event = new CustomEvent('checkUnsavedChanges', {
        detail: { newSection, paramName, replay },
        cancelable: true,
      })
      return document.dispatchEvent(event)
    },
    [shouldCheckUnsavedChanges],
  )

  const urlParams = useMemo(() => {
    const currentParams: Record<string, string | null> = {}
    let hasChanged = false
    for (const [key, value] of searchParams.entries()) {
      if (paramsRef.current[key] !== value) {
        hasChanged = true
      }
      currentParams[key] = value
    }
    for (const key of Object.keys(paramsRef.current)) {
      if (!searchParams.has(key)) {
        hasChanged = true
      }
    }
    if (!hasChanged) {
      pendingSearchRef.current = null
      return paramsRef.current
    }
    paramsRef.current = currentParams
    pendingSearchRef.current = null
    return currentParams
  }, [searchParams])

  const modifyUrlParams = useCallback(
    (
      action: 'set' | 'delete',
      param: string,
      value?: string,
      { replaceCurrentHistoryEntry }: URLReplaceOption = {},
    ) => {
      const updated = new URLSearchParams(pendingSearchRef.current ?? searchParams)

      if (action === 'set' && value !== undefined) {
        const sanitizedValue = sanitizeValue(value)
        if (updated.get(param) === sanitizedValue) return
        updated.set(param, sanitizedValue)
      } else if (action === 'delete') {
        if (!updated.has(param)) return
        updated.delete(param)
      }

      const updatedSearch = updated.toString()
      if (updatedSearch === (pendingSearchRef.current ?? searchParams.toString())) return

      const replay = () => {
        pendingSearchRef.current = updatedSearch
        setSearchParams(updated, { replace: replaceCurrentHistoryEntry })
      }

      if (checkUnsavedChanges(replay, value, param)) replay()
    },
    [searchParams, setSearchParams, checkUnsavedChanges],
  )

  const replaceParams = useCallback(
    ({ remove, update, replaceCurrentHistoryEntry }: ReplaceParamsOptions) => {
      const updatedParams = new URLSearchParams(pendingSearchRef.current ?? searchParams)

      if (remove) {
        remove.forEach((param) => updatedParams.delete(param))
      }
      if (update) {
        update.forEach(([param, value]) => {
          if (value) updatedParams.set(param, sanitizeValue(value))
        })
      }

      const updatedSearch = updatedParams.toString()
      if (updatedSearch === (pendingSearchRef.current ?? searchParams.toString())) return

      const newSection = update?.map(([, paramValue]) => paramValue).join(',')
      const replay = () => {
        pendingSearchRef.current = updatedSearch
        setSearchParams(updatedParams, { replace: replaceCurrentHistoryEntry })
      }

      if (checkUnsavedChanges(replay, newSection)) replay()
    },
    [searchParams, setSearchParams, checkUnsavedChanges],
  )

  const updateParam = useCallback(
    (param: string, value: string, options?: URLReplaceOption) =>
      modifyUrlParams('set', param, value, options),
    [modifyUrlParams],
  )

  const removeParam = useCallback(
    (param: string) => modifyUrlParams('delete', param),
    [modifyUrlParams],
  )

  return { urlParams, updateParam, removeParam, replaceParams }
}
